/**
 * Главная страница `/` (FE-13). Карта региона на всю область контента
 * (redesign): карта — базовый слой, фильтры объектов и статусы — оверлеем.
 *
 * Выбор КАТО в селекторе:
 *  - область / район / сельский округ (узел с детьми) → граница из OSM
 *    Nominatim (у себя не храним) + зум по её bbox; если полигона в OSM
 *    нет — зум по точкам поддерева без границы;
 *  - село (лист дерева) → без границы, просто зум к его точке.
 * Клик по маркеру карточку не открывает — у маркера свой leaflet-попап.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MODULES } from '../../consts/modules';
import { useModulePresent } from '../../hooks/useModulePresent';
import { RegionInfoCard } from '../../components/map/RegionInfoCard';
import { RegionMap, type MapFocus } from '../../components/map/RegionMap';
import { useKato } from '../../contexts/KatoContext';
import {
  getKatoCoordinates,
  getKatoList,
  type KatoListItem,
} from '../../requests/public.request';
import {
  fetchOsmBoundary,
  osmBoundaryQueries,
  type OsmBoundary,
} from '../../requests/osm.request';
import { filterByKatoScope, subtreeKatos } from '../../lib/kato-scope';
import type { KatoCoordinate } from '../../types/public';
import { MapFilters } from './components/MapFilters';

/** Bbox по точкам: [[юг, запад], [север, восток]]; null — точек нет. */
const boundsOfPoints = (
  points: KatoCoordinate[],
): [[number, number], [number, number]] | null => {
  if (points.length === 0) return null;
  let south = Infinity;
  let north = -Infinity;
  let west = Infinity;
  let east = -Infinity;
  for (const point of points) {
    south = Math.min(south, point.latitude);
    north = Math.max(north, point.latitude);
    west = Math.min(west, point.longitude);
    east = Math.max(east, point.longitude);
  }
  return [
    [south, west],
    [north, east],
  ];
};

export const HomePage = () => {
  const { t, i18n } = useTranslation();
  const showRegionCard = useModulePresent(MODULES.home.regionCard);
  const { selectedKato } = useKato();

  const [coords, setCoords] = useState<KatoCoordinate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [katoItems, setKatoItems] = useState<KatoListItem[]>([]);
  const [boundary, setBoundary] = useState<OsmBoundary | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  // КАТО, выбранный кликом по маркеру: для него оверлей не открываем.
  const markerSelectRef = useRef<number | null>(null);
  // КАТО, восстановленный из localStorage при заходе на страницу:
  // для него оверлей по умолчанию закрыт — открываем только при явном
  // выборе пользователем.
  const initialKatoRef = useRef<number | null>(selectedKato);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getKatoCoordinates()
      .then((response) => {
        if (!cancelled) {
          setCoords(response.coordinates);
          setError(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          console.error('HomePage getKatoCoordinates failed', err);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getKatoList()
      .then((response) => {
        if (!cancelled) setKatoItems(response.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) console.error('HomePage getKatoList failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedItem =
    katoItems.find((item) => item.kato === selectedKato) ?? null;

  // Точки на карте: весь список — только для области/без выбора,
  // иначе — поддерево района выбранного КАТО (см. kato-scope.ts).
  const visibleCoords = useMemo(
    () => filterByKatoScope(coords, katoItems, selectedKato),
    [coords, katoItems, selectedKato],
  );

  useEffect(() => {
    if (!selectedItem) {
      setBoundary(null);
      setFocus(null);
      setInfoOpen(false);
      return;
    }
    // Пока дерево КАТО не загружено, село не отличить от округа — ждём.
    if (katoItems.length === 0) return;
    // Выбор пришёл кликом по маркеру — информация уже в leaflet-попапе.
    if (markerSelectRef.current === selectedItem.kato) {
      markerSelectRef.current = null;
      return;
    }

    initialKatoRef.current = null;

    // Село — лист дерева: без границы, просто зум к его точке.
    const isVillage =
      selectedItem.parent_id !== null &&
      !katoItems.some((item) => item.parent_id === selectedItem.kato);
    if (isVillage) {
      setBoundary(null);
      const point = coords.find((c) => c.kato === selectedItem.kato);
      if (point) {
        setFocus({
          kind: 'point',
          center: [point.latitude, point.longitude],
        });
      }
      return;
    }

    // Область / район / сельский округ: граница из OSM + зум по её bbox.
    // Одноимённые единицы в других регионах («Жамбылский район» СКО против
    // Жамбылской области на юге) отсеиваются двумя способами: в запрос
    // добавляется название области, а результат проверяется на вхождение
    // точки территории (своей или первой из поддерева) в bbox.
    const oblast = katoItems.find((item) => item.parent_id === null);
    const queries = osmBoundaryQueries(
      selectedItem.name_ru,
      selectedItem.name_kz,
      selectedItem.parent_id !== null ? oblast?.name_ru : undefined,
    );
    const scope = subtreeKatos(katoItems, selectedItem.kato);
    const scopePoints = coords.filter((c) => scope.has(c.kato));
    const nearPoint =
      scopePoints.find((c) => c.kato === selectedItem.kato) ?? scopePoints[0];
    const near: [number, number] | undefined = nearPoint
      ? [nearPoint.latitude, nearPoint.longitude]
      : undefined;

    let cancelled = false;
    // Варианты — по очереди, до первого полигона: Nominatim ~1 запрос/сек.
    const findBoundary = async (): Promise<OsmBoundary | null> => {
      for (const query of queries) {
        if (cancelled) return null;
        const result = await fetchOsmBoundary(query, near);
        if (result) return result;
      }
      return null;
    };
    findBoundary()
      .then((result) => {
        if (cancelled) return;
        setBoundary(result);
        if (result) {
          setFocus({ kind: 'bounds', bounds: result.bbox });
          return;
        }
        // Полигона в OSM нет (частое для сельских округов) или найденная
        // граница не накрывает точки территории — зум по точкам поддерева.
        const bounds = boundsOfPoints(scopePoints);
        if (bounds) setFocus({ kind: 'bounds', bounds });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('HomePage fetchOsmBoundary failed', err);
          setBoundary(null);
        }
      });
    return () => {
      cancelled = true;
    };
    // selectedItem пересоздаётся каждый рендер — зависимость по kato;
    // katoItems/coords включены: классификация и зум зависят от их загрузки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.kato, katoItems, coords]);

  // Сельский округ — узел ниже района: его населённые пункты выделяются на
  // карте, остальные точки района приглушаются. У района подсвечивать
  // нечего — на карте и так только его поддерево.
  const highlighted = useMemo(() => {
    if (!selectedItem || selectedItem.parent_id === null) return null;
    const parent = katoItems.find((item) => item.kato === selectedItem.parent_id);
    if (!parent || parent.parent_id === null) return null;
    const scope = subtreeKatos(katoItems, selectedItem.kato);
    return scope.size > 1 ? scope : null;
  }, [selectedItem, katoItems]);

  const isKz = i18n.resolvedLanguage === 'kz';

  return (
    <section className="absolute inset-0" data-testid="home-page">
      <RegionMap
        coordinates={visibleCoords}
        boundary={boundary}
        highlighted={highlighted}
        focus={focus}
        onMarkerSelect={(kato) => {
          markerSelectRef.current = kato;
          setInfoOpen(false);
        }}
        height="100%"
      />
      <div className="pointer-events-none absolute inset-x-4 top-3 z-[500] flex flex-col items-start gap-2">
        <MapFilters />
        {loading && coords.length === 0 && (
          <div
            className="pointer-events-auto rounded-lg border border-surface-border bg-surface px-3 py-2 text-xs text-text-muted shadow-card"
            data-testid="home-loading"
          >
            {t('common.loading')}
          </div>
        )}
        {error && (
          <div
            className="pointer-events-auto rounded-lg border border-surface-border bg-surface px-3 py-2 text-xs text-status-danger shadow-card"
            data-testid="home-error"
          >
            {t('common.dataUnavailable')}
          </div>
        )}
      </div>

      {/* Карточка территории по клику на карте — виджет витрины
          (`home.region-card`, US-09 FR-1): выключенная супер-админом не
          показывается, сама карта и фильтры остаются. */}
      {infoOpen && selectedItem && showRegionCard && (
        <div className="absolute left-1/2 top-16 z-[600] -translate-x-1/2">
          <RegionInfoCard
            kato={selectedItem.kato}
            name={isKz ? selectedItem.name_kz : selectedItem.name_ru}
            onClose={() => setInfoOpen(false)}
          />
        </div>
      )}
    </section>
  );
};
